import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import api from "@/lib/api";
import { track, fbEvent } from "@/lib/analytics";

// Session-level dedup so a page refresh doesn't fire `purchase` twice.
const _firedPurchases = new Set();

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", product: "", attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", product: "", attempts: 0 });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!_firedPurchases.has(sessionId)) {
            _firedPurchases.add(sessionId);
            track("purchase", {
              transaction_id: sessionId,
              currency: (data.currency || "usd").toUpperCase(),
              value: Number(data.amount) || 0,
              items: [{
                item_id: data.product_id || data.product,
                item_name: data.product,
                price: Number(data.amount) || 0,
                quantity: 1,
              }],
            });
            fbEvent("Purchase", {
              value: Number(data.amount) || 0,
              currency: (data.currency || "usd").toUpperCase(),
              content_ids: [data.product_id || data.product],
              content_name: data.product,
              content_type: "product",
            });
          }
          setState({ status: "paid", product: data.product, attempts });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", product: data.product, attempts });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ status: "timeout", product: data.product, attempts });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          setState({ status: "error", product: "", attempts });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <SiteLayout branch="main">
      <section className="section max-w-2xl mx-auto text-center" data-testid="checkout-success">
        {state.status === "polling" && (
          <>
            <Loader2 className="animate-spin mx-auto text-[var(--gold)]" size={40} />
            <h1 className="font-display text-4xl text-[var(--navy)] mt-6">Confirming your payment…</h1>
            <p className="text-[var(--muted)] mt-4">This usually takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-green-600" size={56} />
            <h1 className="font-display text-4xl text-[var(--navy)] mt-6" data-testid="success-heading">
              Thank you — payment received.
            </h1>
            <p className="text-[var(--muted)] mt-4">
              Your access to <strong className="text-[var(--navy)]">{state.product}</strong> is now active. A
              confirmation email is on its way.
            </p>
            <div className="flex justify-center gap-3 mt-8">
              <Link to="/dashboard" className="btn btn-navy" data-testid="go-dashboard">Go to Dashboard</Link>
              <Link to="/academy" className="btn btn-ghost">Browse More</Link>
            </div>
          </>
        )}
        {state.status === "expired" && (
          <>
            <XCircle className="mx-auto text-red-600" size={56} />
            <h1 className="font-display text-4xl text-[var(--navy)] mt-6">This session expired.</h1>
            <Link to="/academy" className="btn btn-navy mt-8">Return to Academy</Link>
          </>
        )}
        {(state.status === "timeout" || state.status === "error") && (
          <>
            <Loader2 className="mx-auto text-[var(--muted)]" size={40} />
            <h1 className="font-display text-4xl text-[var(--navy)] mt-6">Payment is still processing…</h1>
            <p className="text-[var(--muted)] mt-4">
              We'll email you as soon as it's confirmed. You can also check your Dashboard.
            </p>
            <Link to="/dashboard" className="btn btn-navy mt-8">Go to Dashboard</Link>
          </>
        )}
      </section>
    </SiteLayout>
  );
}
